import { useEffect, useState } from "react";
import { api, errMsg } from "../lib/api";
import { fmtMoney, fmtDate, PAYMENT_METHODS } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { DataTable } from "../components/DataTable";
import { Modal, Field, inputCls, selectCls, btnPrimary, btnSecondary, btnDanger } from "../components/Modal";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const CATS = ["Transport", "Carburant", "Salaires", "Électricité", "Loyer", "Réparation", "Manutention", "Téléphone", "Taxes", "Frais bancaires", "Autres"];
const EMPTY = { category: "Transport", amount: "", date: "", description: "", beneficiary: "", method: "Espèces" };

export default function Expenses() {
  const { user, hasPerm } = useAuth();
  const cur = user?.currency || "FCFA";
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = () => api.get("/expenses").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    try {
      await api.post("/expenses", { ...form, amount: Number(form.amount) });
      toast.success("Dépense enregistrée");
      setOpen(false); setForm(EMPTY);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async () => {
    try {
      await api.delete(`/expenses/${confirmDel.id}`);
      toast.success("Dépense supprimée (contre-passée en caisse)");
      setConfirmDel(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const total = rows.reduce((s, e) => s + e.amount, 0);

  const columns = [
    { key: "date", label: "Date", render: (e) => fmtDate(e.date) },
    { key: "category", label: "Catégorie", render: (e) => <span className="font-semibold">{e.category}</span> },
    { key: "description", label: "Description" },
    { key: "beneficiary", label: "Bénéficiaire" },
    { key: "method", label: "Mode" },
    { key: "amount", label: "Montant", className: "text-right", render: (e) => <span className="font-money font-bold text-rose-600">{fmtMoney(e.amount, cur)}</span> },
    { key: "created_by_name", label: "Par" },
    ...(hasPerm("expenses.delete") ? [{ key: "actions", label: "", className: "text-right", render: (e) => (
      <button data-testid={`delete-expense-${e.id}`} onClick={() => setConfirmDel(e)} className="h-8 w-8 inline-flex items-center justify-center rounded-md text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950"><Trash2 className="h-4 w-4" /></button>) }] : []),
  ];

  return (
    <div className="space-y-4" data-testid="expenses-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Dépenses</h1>
          <p className="text-sm text-muted-foreground">Total affiché : <span className="font-money font-bold text-rose-600">{fmtMoney(total, cur)}</span></p>
        </div>
        {hasPerm("expenses.create") && <button data-testid="add-expense-btn" onClick={() => setOpen(true)} className={btnPrimary}><Plus className="h-4 w-4" /> Nouvelle dépense</button>}
      </div>
      <DataTable testid="expenses-table" columns={columns} rows={rows} searchKeys={["category", "description", "beneficiary", "created_by_name"]} pageSize={15} />

      <Modal open={open} onClose={() => setOpen(false)} title="Nouvelle dépense">
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Catégorie">
              <select data-testid="exp-category" value={form.category} onChange={set("category")} className={selectCls}>
                {CATS.map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Montant *"><input data-testid="exp-amount" type="number" min="0" value={form.amount} onChange={set("amount")} className={inputCls} /></Field>
            <Field label="Date"><input data-testid="exp-date" type="date" value={form.date} onChange={set("date")} className={inputCls} /></Field>
            <Field label="Mode de paiement">
              <select data-testid="exp-method" value={form.method} onChange={set("method")} className={selectCls}>
                {PAYMENT_METHODS.map((m) => <option key={m}>{m}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Bénéficiaire"><input data-testid="exp-beneficiary" value={form.beneficiary} onChange={set("beneficiary")} className={inputCls} /></Field>
          <Field label="Description"><textarea data-testid="exp-description" value={form.description} onChange={set("description")} className={`${inputCls} h-20 py-2`} /></Field>
          <button data-testid="save-expense" onClick={save} disabled={!Number(form.amount)} className={`${btnPrimary} w-full`}>Enregistrer</button>
        </div>
      </Modal>

      <Modal open={!!confirmDel} onClose={() => setConfirmDel(null)} title="Supprimer la dépense">
        <p className="text-sm">Supprimer la dépense <b>{confirmDel?.category}</b> de <b className="font-money">{fmtMoney(confirmDel?.amount, cur)}</b> ? Le montant sera recrédité en caisse.</p>
        <div className="flex gap-2 mt-4">
          <button data-testid="confirm-delete-expense" onClick={del} className={`${btnDanger} flex-1`}>Supprimer</button>
          <button onClick={() => setConfirmDel(null)} className={btnSecondary}>Annuler</button>
        </div>
      </Modal>
    </div>
  );
}
